/**
 * Near-duplicate detection for company words.
 *
 * Two saved spellings look alike when they match once case, spaces, and
 * hyphens are ignored, or when the longer spelling is at least four letters
 * and the two differ by a single insertion, deletion, or substitution.
 * Short pairs ("AI" / "A", "cat" / "car") are left alone.
 */

const MIN_EDIT_LENGTH = 4

/** Lowercase, with spaces and hyphens removed. */
export function normalizeCompanyWord(term: string): string {
  return term.toLowerCase().replace(/[\s-]+/g, "")
}

/** Levenshtein distance. Company words are short, so the full table is fine. */
export function editDistance(left: string, right: string): number {
  if (left === right) return 0
  const rows = left.length + 1
  const cols = right.length + 1
  const prev: number[] = []
  const curr: number[] = []
  for (let col = 0; col < cols; col++) prev[col] = col
  for (let row = 1; row < rows; row++) {
    curr[0] = row
    const leftChar = left[row - 1]
    for (let col = 1; col < cols; col++) {
      const cost = leftChar === right[col - 1] ? 0 : 1
      const del = (prev[col] ?? 0) + 1
      const ins = (curr[col - 1] ?? 0) + 1
      const sub = (prev[col - 1] ?? 0) + cost
      curr[col] = Math.min(del, ins, sub)
    }
    for (let col = 0; col < cols; col++) prev[col] = curr[col] ?? 0
  }
  return prev[cols - 1] ?? 0
}

/** True when the two spellings would confuse someone reading the list. */
export function areNearDuplicates(left: string, right: string): boolean {
  if (left === right) return false
  const a = normalizeCompanyWord(left)
  const b = normalizeCompanyWord(right)
  if (a.length === 0 || b.length === 0) return false
  if (a === b) return true
  if (Math.max(a.length, b.length) < MIN_EDIT_LENGTH) return false
  return editDistance(a, b) <= 1
}

/**
 * For each term, the first other term it resembles, in list order.
 * A term with no match is absent from the map.
 */
export function findNearDuplicates(terms: readonly string[]): ReadonlyMap<string, string> {
  const matches = new Map<string, string>()
  for (let i = 0; i < terms.length; i++) {
    const term = terms[i]
    if (term === undefined || matches.has(term)) continue
    for (let j = 0; j < terms.length; j++) {
      if (i === j) continue
      const other = terms[j]
      if (other === undefined) continue
      if (areNearDuplicates(term, other)) {
        matches.set(term, other)
        break
      }
    }
  }
  return matches
}
